import { useCallback, useEffect, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { useApiFetch } from '../api';
import type { ProductCategory } from '../types/models/ProductCategory';
import type { ApiResponseProductCategoryList } from '../types/api/ApiResponseAdminProduct';

interface UseProductCategoriesReturn {
  categories: ProductCategory[];
  loading: boolean;
  /** Human-readable error message (the backend's JSend `message`), or null. */
  error: string | null;
  refetch: () => Promise<void>;
}

// GET /admin/products/categories — all product categories, ordered by id. Lazy: `enabled` (e.g. a
// modal's isOpen) defers the fetch until it's needed. Staff-only on the backend; useApiFetch injects
// the token. Create/rename/delete mutations are issued inline by the consuming modal, which calls
// refetch() afterwards.
export const useProductCategories = (enabled = true): UseProductCategoriesReturn => {
  const [categories, setCategories] = useState<ProductCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const auth = useAuth();
  const apiFetch = useApiFetch();

  const fetchCategories = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiFetch('/admin/products/categories');
      // Branch on the JSend body.status, NEVER response.ok.
      const body = (await response.json().catch(() => null)) as ApiResponseProductCategoryList | null;
      if (body?.status === 'success' && body.data?.categories) {
        setCategories(body.data.categories);
      } else {
        setError(body?.message ?? 'Failed to load categories');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred while fetching categories');
    } finally {
      setLoading(false);
    }
  }, [apiFetch]);

  useEffect(() => {
    if (enabled && auth.isAuthenticated) {
      void fetchCategories();
    }
  }, [enabled, auth.isAuthenticated, fetchCategories]);

  return { categories, loading, error, refetch: fetchCategories };
};
